import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Quick-exit calculator disguise.
 * Looks and behaves like a normal calculator. Holding the "=" key for 1 second
 * silently returns the user to Sister Signal (/today).
 */
export default function Calculator() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [display, setDisplay] = useState("0");
  const [previous, setPrevious] = useState(null);
  const [op, setOp] = useState(null);
  const [overwrite, setOverwrite] = useState(true);
  const pressTimer = useRef(null);
  const heldOpenedRef = useRef(false);

  const press = (label) => {
    if (/[0-9]/.test(label)) {
      setDisplay((d) => (overwrite ? label : d === "0" ? label : d + label));
      setOverwrite(false);
    } else if (label === ".") {
      setDisplay((d) => (overwrite ? "0." : d.includes(".") ? d : d + "."));
      setOverwrite(false);
    } else if (label === "C") {
      setDisplay("0"); setPrevious(null); setOp(null); setOverwrite(true);
    } else if (label === "+/-") {
      setDisplay((d) => (d.startsWith("-") ? d.slice(1) : d === "0" ? d : "-" + d));
    } else if (label === "%") {
      setDisplay((d) => String(parseFloat(d) / 100));
    } else if (["+", "−", "×", "÷"].includes(label)) {
      setPrevious(parseFloat(display)); setOp(label); setOverwrite(true);
    } else if (label === "=") {
      if (previous !== null && op !== null) {
        const a = previous; const b = parseFloat(display);
        const r = op === "+" ? a + b : op === "−" ? a - b : op === "×" ? a * b : a / b;
        setDisplay(String(Number.isFinite(r) ? +r.toFixed(8) : "Error"));
        setPrevious(null); setOp(null); setOverwrite(true);
      }
    }
  };

  // Long-press on "=" returns to Sister Signal.
  const onEqDown = () => {
    heldOpenedRef.current = false;
    pressTimer.current = setTimeout(() => {
      heldOpenedRef.current = true;
      navigate("/today", { replace: true });
    }, 1000);
  };
  const onEqUp = () => {
    clearTimeout(pressTimer.current);
    if (!heldOpenedRef.current) press("=");
  };
  const onEqLeave = () => { clearTimeout(pressTimer.current); };

  useEffect(() => () => clearTimeout(pressTimer.current), []);

  // Match basic iOS dark calculator look — looks ordinary, draws no attention
  const Btn = ({ label, type = "num", onMouseDown, onMouseUp, onMouseLeave, testid }) => {
    const styles = {
      num: "bg-zinc-700 text-white",
      fn:  "bg-zinc-500 text-black",
      op:  "bg-orange-500 text-white",
    };
    return (
      <button
        data-testid={testid || `calc-${label}`}
        onClick={() => !onMouseDown && press(label)}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onMouseDown}
        onTouchEnd={onMouseUp}
        className={`h-16 rounded-full text-2xl font-light active:opacity-80 ${styles[type]}`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="min-h-[100dvh] w-full bg-black text-white" data-testid="page-calculator">
      <div className="mx-auto w-full max-w-md min-h-[100dvh] flex flex-col px-4 pt-16 pb-6">
        <div className="flex-1 flex items-end justify-end px-2 pb-3">
          <div data-testid="calc-display" className="text-7xl font-light truncate">{display}</div>
        </div>
        <div className="grid grid-cols-4 gap-2.5">
          <Btn label="C" type="fn" />
          <Btn label="+/-" type="fn" />
          <Btn label="%" type="fn" />
          <Btn label="÷" type="op" />

          <Btn label="7" />
          <Btn label="8" />
          <Btn label="9" />
          <Btn label="×" type="op" />

          <Btn label="4" />
          <Btn label="5" />
          <Btn label="6" />
          <Btn label="−" type="op" />

          <Btn label="1" />
          <Btn label="2" />
          <Btn label="3" />
          <Btn label="+" type="op" />

          <button
            data-testid="calc-0"
            onClick={() => press("0")}
            className="col-span-2 h-16 rounded-full bg-zinc-700 text-white text-2xl text-left pl-7 active:opacity-80"
          >0</button>
          <Btn label="." />
          <Btn
            label="="
            type="op"
            testid="calc-equals"
            onMouseDown={onEqDown}
            onMouseUp={onEqUp}
            onMouseLeave={onEqLeave}
          />
        </div>
        <p
          data-testid="calc-return-hint"
          className="text-center text-[10px] tracking-wide text-zinc-700 mt-4 select-none"
        >
          {t("calc.return_hint")}
        </p>
      </div>
    </div>
  );
}
